import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import profesionService from '../services/profesionService';
import '../styles/Professions.css';
import '../styles/theme/toast.css';

const Professions = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [currentProfession, setCurrentProfession] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [formErrors, setFormErrors] = useState({});
  const [showToast, setShowToast] = useState(false);

  // Cargar profesiones
  useEffect(() => {
    fetchProfessions();
  }, []);

  const fetchProfessions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await profesionService.getAllProfesiones();
      setProfessions(data || []);
    } catch (error) {
      console.error('Error al obtener profesiones:', error);
      setError('Error al cargar profesiones: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const validateProfessionForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre de la profesión es requerido';
    } else if (formData.name.length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    } else if (formData.description.length < 10) {
      newErrors.description = 'La descripción debe tener al menos 10 caracteres';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error cuando el usuario empiece a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCreate = () => {
    setFormData({ name: '', description: '' });
    setCurrentProfession(null);
    setFormErrors({});
    setShowForm(true);
  };

  const handleEdit = async (id) => {
    try {
      const profesion = professions.find(p => p.PROFESION_ID === id);
      if (profesion) {
        setFormData({
          name: profesion.PROFESION_NOMBRE || '',
          description: profesion.PROFESION_OBSERVACION || ''
        });
        setCurrentProfession(profesion);
        setFormErrors({});
        setShowForm(true);
      }
    } catch (error) {
      console.error('Error al cargar profesión:', error);
      alert('Error al cargar la profesión');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfessionForm()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const profesionData = {
        PROFESION_NOMBRE: formData.name.trim(),
        PROFESION_OBSERVACION: formData.description.trim()
      };
      
      if (currentProfession) {
        // Actualizar profesión existente
        await profesionService.updateProfesion(currentProfession.PROFESION_ID, profesionData);
        alert('Profesión actualizada exitosamente');
      } else {
        // Crear nueva profesión
        await profesionService.createProfesion(profesionData);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      }
      
      await fetchProfessions();
      resetForm();
    } catch (error) {
      console.error('Error al guardar profesión:', error);
      setError('Error al guardar profesión: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setCurrentProfession(null);
    setShowForm(false);
    setFormErrors({});
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de que desea desactivar esta profesión?')) {
      try {
        setLoading(true);
        setError('');
        await profesionService.deleteProfesion(id);
        // Recargar la lista después de desactivar
        await fetchProfessions();
      } catch (error) {
        console.error('Error al desactivar profesión:', error);
        setError('Error al desactivar profesión: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleActivate = async (id) => {
    if (window.confirm('¿Está seguro de que desea activar esta profesión?')) {
      try {
        setLoading(true);
        setError('');
        
        await profesionService.activateProfesion(id);
        
        // Recargar la lista de profesiones
        await fetchProfessions();
      } catch (error) {
        console.error('Error al activar profesión:', error);
        setError('Error al activar profesión: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredProfessions = professions.filter(profession => 
    (profession.PROFESION_ID || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    (profession.PROFESION_NOMBRE || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (profession.PROFESION_OBSERVACION || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeProfessions = filteredProfessions.filter(p => (p.PROFESION_ESTADO || 'Activo').toLowerCase() === 'activo');

  const total = filteredProfessions.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginatedProfessions = filteredProfessions.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      {showToast && (
        <div className="custom-toast custom-toast-success">
          ¡Profesión registrada exitosamente!
        </div>
      )}
      <div className="professions-container">
        <div className="page-header">
          <div className="header-title">
            <h1>Gestión de Profesiones</h1>
            <p>Administra las profesiones del sistema médico</p>
          </div>
        </div>

        {loading && (
          <div className="loading-message">
            <i className="fas fa-spinner fa-spin"></i> Cargando profesiones...
          </div>
        )}

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i> {error}
          </div>
        )}

        <div className="professions-actions">
          <div className="search-section">
            <div className="search-input">
              <input
                type="text"
                placeholder="Buscar por código, nombre o descripción..."
                value={searchTerm}
                onChange={handleSearch}
              />
              <button type="button" title="Buscar">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
          
          <button 
            className="btn-primary create-btn" 
            onClick={handleCreate}
            disabled={loading}
          >
            <i className="fas fa-plus"></i>
            Registrar Profesión
          </button>
        </div>

        <div className="professions-table-container">
          <table className="professions-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProfessions.map(profession => (
                <tr key={profession.PROFESION_ID}>
                  <td><span className="code-id-field">{profession.PROFESION_ID}</span></td>
                  <td>{profession.PROFESION_NOMBRE}</td>
                  <td>{profession.PROFESION_OBSERVACION}</td>
                  <td>
                    <span className={`status-badge ${(profession.PROFESION_ESTADO || 'activo').toLowerCase()}`}>
                      {profession.PROFESION_ESTADO || 'Activo'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn"
                      onClick={() => handleEdit(profession.PROFESION_ID)}
                      title="Editar"
                      disabled={loading}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    {(profession.PROFESION_ESTADO || 'Activo').toLowerCase() === 'activo' ? (
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(profession.PROFESION_ID)}
                        title="Desactivar"
                        disabled={loading}
                      >
                        <i className="fas fa-ban"></i>
                      </button>
                    ) : (
                      <button 
                        className="action-btn activate-btn"
                        onClick={() => handleActivate(profession.PROFESION_ID)}
                        title="Activar"
                        disabled={loading}
                      >
                        <i className="fas fa-check-circle"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            Mostrando <strong>{paginatedProfessions.length}</strong> de <strong>{total}</strong> profesiones
            ({activeProfessions.length} activas)
          </div>
          <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
            <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
            <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
            <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Por página:</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </label>
          </div>
        </div>

        {showForm && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  <i className="fas fa-briefcase"></i>
                  {currentProfession ? 'Editar Profesión' : 'Crear Nueva Profesión'}
                </h3>
                <button className="modal-close" onClick={resetForm}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form className="profession-form" onSubmit={handleSubmit}>
                <div className="form-section">
                  <h3>Información de la Profesión</h3>
                  
                  <div className="form-group">
                    <label htmlFor="name">
                      Nombre de la Profesión <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={formErrors.name ? 'error' : ''}
                      placeholder="Ej: Médico General"
                      disabled={loading}
                    />
                    {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">
                      Descripción <span className="required">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className={formErrors.description ? 'error' : ''}
                      placeholder="Describe las funciones..."
                      rows="4"
                      disabled={loading}
                    />
                    {formErrors.description && <span className="error-message">{formErrors.description}</span>}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={resetForm} disabled={loading}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Guardando...
                      </>
                    ) : (
                      currentProfession ? 'Guardar Cambios' : 'Crear Profesión'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Professions;